/**
 * Process Modal Component
 * 
 * A modal component that displays a process step with detailed information.
 * Features animated entrance, hover effects, and a detailed modal view with
 * additional information and links.
 * 
 * Features:
 * - Animated entrance and hover effects
 * - Detailed modal view
 * - Responsive grid layout
 * - Process step indicators
 * - External links support
 */

'use client';

import { motion } from 'framer-motion';
import { Modal, ModalBody, ModalContent, ModalTrigger } from '@/components/ui/animated-modal';
import { LucideIcon, ChevronRight, ExternalLink } from 'lucide-react';
import Link from 'next/link';

/**
 * Props interface for the ProcessModal component
 * @interface ProcessModalProps
 * @property {string} title - Title of the process step
 * @property {string} description - Description of the process step
 * @property {LucideIcon} icon - Icon component for the process step
 * @property {Array<Object>} details - Array of detail items to display in the modal
 * @property {number} index - Current step index
 * @property {number} totalSteps - Total number of steps
 * @property {string} [className] - Additional CSS classes
 */
interface ProcessModalProps {
	title: string;
	description: string;
	icon: LucideIcon;
	details: {
		title: string;
		description: string;
		icon: LucideIcon;
		link?: {
			text: string;
			href: string;
		};
	}[];
	index: number;
	totalSteps: number;
	className?: string;
}

/**
 * Process Modal Component
 * 
 * @component
 * @param {ProcessModalProps} props - Component props
 * @returns {JSX.Element} The process modal component
 * 
 * @example
 * <ProcessModal
 *   title="Step 1"
 *   description="Initial consultation"
 *   icon={ConsultationIcon}
 *   details={[
 *     {
 *       title: "Discovery",
 *       description: "Understand your needs",
 *       icon: DiscoveryIcon,
 *       link: {
 *         text: "Learn more",
 *         href: "/process/discovery"
 *       }
 *     }
 *   ]}
 *   index={0}
 *   totalSteps={4}
 * />
 */
export function ProcessModal({
	title,
	description,
	icon: Icon,
	details,
	index,
	totalSteps,
}: ProcessModalProps) {
	return (
		<div className="relative w-full">
			<Modal>
				<ModalTrigger className="w-full">
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{
							duration: 0.5,
							delay: index * 0.1,
							type: 'spring',
							stiffness: 400,
							damping: 25,
						}}
						whileHover={{
							y: -8,
							scale: 1.02,
							transition: { duration: 0.2 },
						}}
						className="space-card px-5 py-8 rounded-xl flex flex-col h-[420px] w-full cursor-pointer hover:shadow-[0_0_30px_rgba(255,255,255,0.1)] transition-all duration-300 bg-black/40 relative group mt-2 mb-2"
					>
						<div className="absolute inset-0 rounded-xl bg-gradient-to-br from-orange-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
						<div className="w-16 h-16 rounded-full mb-4 flex items-center justify-center bg-orange-500/10 mx-auto group-hover:scale-110 transition-transform duration-300">
							<Icon className="w-8 h-8 text-red-500" />
						</div>
						<div className="text-center flex flex-col flex-grow">
							<h3 className="text-xl font-bold mb-2 whitespace-normal hyphens-auto break-words">
								{title}
							</h3>
							<p className="text-muted-foreground text-sm leading-relaxed break-words">
								{description}
							</p>
						</div>
					</motion.div>
				</ModalTrigger>
				<ModalBody>
					<ModalContent className="pb-8">
						{/* Close Button */}
						<button
							onClick={() => window.dispatchEvent(new CustomEvent('close-modal'))}
							onTouchEnd={(e) => {
								e.preventDefault();
								window.dispatchEvent(new CustomEvent('close-modal'));
							}}
							className="absolute top-4 right-4 p-4 -m-4 touch-manipulation z-50"
							aria-label="Close modal"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
								className="text-black dark:text-white h-6 w-6 hover:scale-125 hover:rotate-3 transition duration-200"
								aria-hidden="true"
							>
								<path stroke="none" d="M0 0h24v24H0z" fill="none" />
								<path d="M18 6l-12 12" />
								<path d="M6 6l12 12" />
							</svg>
						</button>

						{/* Modal Header */}
						<div className="text-center mb-8">
							<div className="w-16 h-16 rounded-full mb-4 flex items-center justify-center bg-primary/10 mx-auto">
								<Icon className="w-8 h-8 text-red-500" />
							</div>
							<h3 className="text-2xl font-bold mb-2">{title}</h3>
							<p className="text-muted-foreground">{description}</p>
						</div>

						{/* Details Grid */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							{details.map((detail, index) => (
								<motion.div
									key={detail.title}
									initial={{ scale: 0.98, y: 5 }}
									animate={{ scale: 1, y: 0 }}
									transition={{
										duration: 0.2,
										delay: index * 0.03,
										ease: 'easeOut',
									}}
									className="space-card p-4 rounded-lg"
								>
									<div className="flex items-center gap-3 mb-3">
										<div className="w-10 h-10 rounded-full flex items-center justify-center bg-primary/10">
											<detail.icon className="w-5 h-5 text-red-500" />
										</div>
										<h4 className="font-semibold">{detail.title}</h4>
									</div>
									<p className="text-sm text-muted-foreground mb-2">{detail.description}</p>
									{detail.link && (
										<Link
											href={detail.link.href}
											className="inline-flex items-center gap-2 text-sm text-purple-500 hover:text-purple-500/80 transition-colors mt-2"
										>
											{detail.link.text}
											<ExternalLink className="w-4 h-4" />
										</Link>
									)}
								</motion.div>
							))}
						</div>
					</ModalContent>
				</ModalBody>
			</Modal>
			{/* Step Indicator */}
			{index < totalSteps - 1 && (
				<div className="hidden lg:block absolute top-1/2 right-[-8px] transform translate-x-1/2 -translate-y-1/2 z-10">
					<ChevronRight className="w-6 h-6 text-red-500" />
				</div>
			)}
		</div>
	);
}
